"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import PhotoFrame from "./PhotoFrame";
import BottleIllustration from "./BottleIllustration";

type Tone = "light" | "dark" | "mid" | "bright" | "macro";

export interface HeroSlide {
  kw: string;
  id: string;
  sub: string;
  tone: Tone;
  href: string;
  photo?: string;
  bottleLabel: string;
}

interface EditorialHeroProps {
  slides: HeroSlide[];
  collectionLabel?: string;
  headline: React.ReactNode;
  studyHref?: string;
  shopLabel?: string;
  studyLabel?: string;
  i18n?: {
    featuredLabel?: string;       // "FEATURED"
    editorialPrefix?: string;     // "EDITORIAL" prefix for the photo label
    specLabel?: string;           // "SPEC"
    euCompliantLabel?: string;    // "EU COMPLIANT"
    lotLabel?: string;            // "LOT"
  };
}

export default function EditorialHero({
  slides,
  collectionLabel = "COLLECTION 04 — SS26",
  headline,
  studyHref = "#",
  shopLabel = "SHOP",
  studyLabel = "Read Article ↗",
  i18n,
}: EditorialHeroProps) {
  const t = {
    featuredLabel: i18n?.featuredLabel ?? "FEATURED",
    editorialPrefix: i18n?.editorialPrefix ?? "EDITORIAL",
    specLabel: i18n?.specLabel ?? "SPEC",
    euCompliantLabel: i18n?.euCompliantLabel ?? "EU COMPLIANT",
    lotLabel: i18n?.lotLabel ?? "LOT",
  };
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => setFrame((f) => (f + 1) % slides.length), 5200);
    return () => clearInterval(id);
  }, [slides.length]);

  const s = slides[frame];

  return (
    <section
      style={{
        padding: "48px var(--bp-edge) 80px",
        borderBottom: "1px solid var(--bp-rule)",
      }}
    >
      <div className="bp-hero-grid">
        {/* Left column — headline & meta */}
        <div style={{ display: "flex", flexDirection: "column", gap: 40, paddingTop: 12 }}>
          <div className="bp-mono" style={{ color: "var(--bp-ink-2)" }}>
            <span className="bp-bracket">{collectionLabel}</span>
          </div>

          <h1
            style={{
              fontFamily: "var(--bp-serif)",
              fontWeight: 300,
              letterSpacing: "-0.025em",
              fontSize: "clamp(52px, 7vw, 104px)",
              lineHeight: 0.98,
              margin: 0,
              textWrap: "balance" as never,
            }}
          >
            {headline}
          </h1>

          <div
            style={{
              marginTop: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 14,
              paddingTop: 20,
              borderTop: "1px solid var(--bp-rule)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span className="bp-mono-sm">{t.featuredLabel}</span>
              <span style={{ fontFamily: "var(--bp-mono)", fontSize: 12 }}>{s.id}</span>
            </div>
            <p
              style={{
                fontSize: 15,
                color: "var(--bp-ink-2)",
                maxWidth: 420,
                margin: "0 0 24px",
              }}
            >
              {s.sub}
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link href={s.href} className="bp-btn">
                {shopLabel} {s.kw}
              </Link>
              <Link href={studyHref} className="bp-btn-ghost">
                {studyLabel}
              </Link>
            </div>
          </div>
        </div>

        {/* Right column — media well */}
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div style={{ position: "relative" }}>
            <PhotoFrame
              tone={s.tone}
              ratio="3/4"
              src={s.photo}
              alt={s.id}
              priority
              sizes="(max-width:900px) 100vw, 55vw"
              label={`${t.editorialPrefix} / ${s.kw}`}
              corner={`FR-${String(frame + 1).padStart(2, "0")}/${String(slides.length).padStart(2, "0")}`}
            />

            {/* Bottle illustration inset (lower-right, breaks the frame) */}
            <div
              style={{
                position: "absolute",
                right: -24,
                bottom: -24,
                width: "32%",
                background: "var(--bp-bg)",
                padding: 20,
                border: "1px solid var(--bp-rule)",
              }}
            >
              <BottleIllustration label={s.bottleLabel} />
            </div>

            {/* HUD crosshairs */}
            <span className="bp-xhair" style={{ position: "absolute", left: "22%", top: "30%" }} />
            <span className="bp-xhair" style={{ position: "absolute", right: "28%", top: "54%" }} />

            {/* Frame indicator */}
            <div style={{ position: "absolute", left: 20, bottom: 20, display: "flex", gap: 6 }}>
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setFrame(i)}
                  aria-label={`Show slide ${i + 1}`}
                  style={{
                    width: 24,
                    height: 1.5,
                    background: i === frame ? "var(--bp-ink)" : "var(--bp-rule)",
                    transition: "background 200ms",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 24,
              padding: "16px 0",
              borderTop: "1px solid var(--bp-rule)",
            }}
          >
            <SpecCell label={t.specLabel} value="pH 5.5 · 30ML · AIRLESS" />
            <SpecCell label={t.euCompliantLabel} value="1223/2009 · 100%" />
            <SpecCell label={t.lotLabel} value="260412·DE" />
          </div>
        </div>
      </div>

      <style>{`
        .bp-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1.15fr;
          gap: 64px;
          align-items: stretch;
        }
        @media (max-width: 900px) {
          .bp-hero-grid {
            grid-template-columns: 1fr;
            gap: 48px;
          }
        }
      `}</style>
    </section>
  );
}

function SpecCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="bp-mono-sm">{label}</div>
      <div style={{ fontFamily: "var(--bp-mono)", fontSize: 12, color: "var(--bp-ink)", marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}
